import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, PropsWithChildren } from 'react';
import { AppContext } from '../window';
import { safeLocalStorage } from '../utils/storage';

interface ThemeContextType {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { react } = useContext(AppContext);
  const appDefaultTheme = react?.theme || 'light';
  
  // Get browser/system preference
  const getSystemTheme = (): 'light' | 'dark' => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  };
  
  // Initialize theme with preference hierarchy
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    // 1. Check localStorage for user preference
    const savedTheme = safeLocalStorage.getItem('theme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme;
    }
    
    // 2. Check browser/system preference
    const systemTheme = getSystemTheme();
    
    // 3. Fall back to app default
    return systemTheme !== 'light' ? systemTheme : appDefaultTheme;
  });

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't set a preference
      if (!safeLocalStorage.getItem('theme')) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    // Add listener for system theme changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }
    
    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Update localStorage and DOM when theme changes
  useEffect(() => {
    safeLocalStorage.setItem('theme', theme);
    // Apply theme to the document root element
    document.documentElement.setAttribute('data-bs-theme', theme);
    // Also apply to body for any components that might need it
    document.body.setAttribute('data-bs-theme', theme);
    
    // Add RSuite theme class for proper dark mode support
    if (theme === 'dark') {
      document.body.classList.add('rs-theme-dark');
    } else {
      document.body.classList.remove('rs-theme-dark');
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  }, []);

  const contextValue = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};